import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Grid,
  FormHelperText,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DialogHeader from "app/components/common/DialogHeader";
import Box from "@mui/material/Box";

import { useFormik } from "formik";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import * as yup from "yup";
import {
  addRequiredDocument,
  editRequiredDocument,
} from "app/services/student-management";
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// Dummy API functions (replace with real API calls)

const availableClasses = [
  "Nursery-ALL",
  "LKG-ALL",
  "1st-ALL",
  "2nd",
  "3rd",
  "4th",
  "5th",
  "6th",
  "7th",
  "8th",
  "9th",
  "10th",
  "11th",
  "12th",
];

const validationSchema = yup.object({
  documentCode: yup.string().required("Code is required"),
  documentName: yup.string().required("Document name is required"),
  isMandatory: yup.boolean().required("Mandatory field is required"),
});

const AddDocumentDialog = ({
  open,
  edit,
  editData,
  onClose,
  refetch = () => {},
}) => {
  const {
    values,
    handleChange,
    setValues,
    touched,
    errors,
    handleSubmit,
    resetForm,
  } = useFormik({
    initialValues: {
      documentName: "",
      documentCode: "",
      classes: [],
      isMandatory: false,
      lastDateOfSubmission: "",
    },
    validationSchema,
    onSubmit: (formValues) => {
      // Prepare payload with required fields
      const payload = {
        documentCode: formValues.documentCode.toUpperCase(),
        documentName: formValues.documentName.toUpperCase(),
        isMandatory: formValues.isMandatory,
      };

      if (edit) {
        // Add documentSettingDocId for edit operation
        payload.documentSettingDocId = editData.documentsettingDocId;
        updateDocumentMutate({ payload });
      } else {
        addDocumentMutate(payload);
      }
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (open) {
      if (edit && editData) {
        setValues({
          documentName: editData.documentName || "",
          documentCode: editData.documentCode || "",
          classes: editData.className || [],
          isMandatory: editData.isMandatory || false,
          remark: editData.remark || "",
          lastDateOfSubmission: editData.lastDateOfSubmission || "",
        });
      } else {
        resetForm();
      }
    }
    // eslint-disable-next-line
  }, [open, edit, editData]);

  // Add document mutation
  const { mutate: addDocumentMutate, isLoading: addDocumentLoading } =
    useMutation("addRequiredDocument", addRequiredDocument, {
      onSuccess: ({ data }) => {
        toast.success(data?.message);
        resetForm();
        refetch();
      },
      onError: ({ response: { data } }) => {
        toast.error(data?.message);
      },
    });

  // Update document mutation
  const { mutate: updateDocumentMutate, isLoading: updateDocumentLoading } =
    useMutation(({ payload }) => editRequiredDocument(payload), {
      onSuccess: (res) => {
        toast.success(res?.data?.message || "Document updated successfully");
        resetForm();
        onClose();
        refetch();
      },
      onError: (err) => {
        toast.error(
          err?.response?.data?.message || "Failed to update document"
        );
      },
    });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "10px",
          maxWidth: "500px",
        },
      }}
    >
      <DialogHeader
        title={edit ? "Edit Document" : "Add More Document"}
        handleClose={onClose}
      />
      <DialogContent sx={{ py: 3 }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Code{" "}
              <Box component="span" sx={{ color: "red", ml: 0.5 }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              name="documentCode"
              value={values.documentCode}
              onChange={handleChange}
              error={touched.documentCode && Boolean(errors.documentCode)}
              helperText={touched.documentCode && errors.documentCode}
              size="small"
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Document Name{" "}
              <Box component="span" sx={{ color: "red", ml: 0.5 }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              name="documentName"
              value={values.documentName}
              onChange={handleChange}
              error={touched.documentName && Boolean(errors.documentName)}
              helperText={touched.documentName && errors.documentName}
              size="small"
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Mandatory{" "}
              <Box component="span" sx={{ color: "red", ml: 0.5 }}>
                *
              </Box>
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                row
                name="isMandatory"
                value={values.isMandatory}
                onChange={(e) => {
                  handleChange({
                    target: {
                      name: "isMandatory",
                      value: e.target.value === "true",
                    },
                  });
                }}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label="Yes"
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="No"
                />
              </RadioGroup>
            </FormControl>
            {touched.isMandatory && errors.isMandatory && (
              <FormHelperText error>{errors.isMandatory}</FormHelperText>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Remarks
            </Typography>
            <TextField
              fullWidth
              name="remark"
              value={values.remark}
              onChange={handleChange}
              multiline
              rows={2}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          pt: 0,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <LoadingButton
          onClick={handleSubmit}
          variant="contained"
          loading={addDocumentLoading || updateDocumentLoading}
          sx={{
            borderRadius: 1,
            textTransform: "uppercase",
            width: { xs: "100%", sm: "60%", md: "48%", lg: "40%" },
            py: 1,
            bgcolor: "rgba(39, 137, 253, 1)",
            "&:hover": {
              bgcolor: "rgba(39, 137, 253, 0.9)",
            },
          }}
        >
          {edit ? "Update" : "ADD"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddDocumentDialog;
